import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as TokenIcon,
  Science as SandboxIcon,
  Verified as ProductionIcon,
} from '@mui/icons-material';
import { settingsService, SispTokenization } from '../../../services';

type Environment = 'sandbox' | 'production';

const SispTokenizationPage: React.FC = () => {
  const { t } = useTranslation();
  const [tokenizations, setTokenizations] = useState<SispTokenization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selected, setSelected] = useState<SispTokenization | null>(null);
  const [activeTab, setActiveTab] = useState<Environment>('sandbox');
  const [formData, setFormData] = useState({
    pos: '',
    posAuthCode: '',
    requestUrl: '',
    cancelUrl: '',
    paymentUrl: '',
    environment: 'sandbox' as Environment,
  });

  const fetchTokenizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getSispTokenization();
      setTokenizations(response.data || []);
    } catch (err: any) {
      console.error('Error fetching SISP tokenization:', err);
      setError(err.message || t('settings.sispTokenization.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTokenizations();
  }, [fetchTokenizations]);

  // Filter tokenizations by active tab
  const filteredTokenizations = tokenizations.filter(t => {
    const env = t.environment?.toLowerCase() || 'production';
    return env === activeTab || (activeTab === 'production' && (env === 'produção' || env === 'production'));
  });

  const sandboxCount = tokenizations.filter(t => t.environment?.toLowerCase() === 'sandbox').length;
  const productionCount = tokenizations.filter(t => {
    const env = t.environment?.toLowerCase() || 'production';
    return env === 'production' || env === 'produção';
  }).length;

  /**
   * URLs de tokenização por ambiente.
   *
   * Os antigos defaults (pos.sisp.cv / mc.sisp.cv, porta 8443) apontavam para hosts
   * descontinuados que já nem resolvem em DNS. Os valores abaixo são os que estão
   * efectivamente em uso em pg_sisp_tokenization nos dois ambientes.
   *
   * Note-se que os nomes dos endpoints diferem entre ambientes: em produção o
   * cancelamento é /Cancel, em sandbox é /TokenCancel.
   */
  const getDefaultUrls = (env: Environment) => {
    if (env === 'sandbox') {
      return {
        requestUrl: 'https://mc.vinti4net.cv/BizMPIOnUsSisp/TokenEnrollment',
        cancelUrl: 'https://mc.vinti4net.cv/BizMPIOnUsSisp/TokenCancel',
        paymentUrl: 'https://mc.vinti4net.cv/BizMPIOnUsSisp/TokenPayment',
      };
    }
    return {
      requestUrl: 'https://www.vinti4net.cv/bizmpi/TokenEnrollment',
      cancelUrl: 'https://www.vinti4net.cv/bizmpi/Cancel',
      paymentUrl: 'https://www.vinti4net.cv/bizmpi/TokenPayment',
    };
  };

  const handleOpenEdit = (tokenization?: SispTokenization) => {
    if (tokenization) {
      setSelected(tokenization);
      const env = tokenization.environment?.toLowerCase();
      setFormData({
        pos: tokenization.pos,
        posAuthCode: tokenization.posAuthCode,
        requestUrl: tokenization.requestUrl,
        cancelUrl: tokenization.cancelUrl,
        paymentUrl: tokenization.paymentUrl,
        environment: (env === 'sandbox' ? 'sandbox' : 'production') as Environment,
      });
    } else {
      setSelected(null);
      const defaultUrls = getDefaultUrls(activeTab);
      setFormData({
        pos: '',
        posAuthCode: '',
        ...defaultUrls,
        environment: activeTab,
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const dataToSave = {
        ...formData,
        environment: formData.environment,
      };
      if (selected) {
        await settingsService.updateSispTokenization(selected.id, dataToSave);
      } else {
        await settingsService.createSispTokenization(dataToSave);
      }
      setEditDialog(false);
      setSelected(null);
      fetchTokenizations();
    } catch (err: any) {
      setError(err.message || t('settings.sispTokenization.errorSaving'));
    }
  };

  const handleDelete = async (tokenization: SispTokenization) => {
    if (!window.confirm(t('settings.sispTokenization.confirmDelete', { name: tokenization.pos }))) {
      return;
    }

    try {
      setError(null);
      await settingsService.deleteSispTokenization(tokenization.id);
      fetchTokenizations();
    } catch (err: any) {
      setError(err.message || t('settings.sispTokenization.errorDeleting'));
    }
  };

  if (loading && tokenizations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('settings.sispTokenization.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('settings.sispTokenization.newConfig')}
        </Button>
      </Box>

      {/* Environment Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="sandbox"
            icon={<SandboxIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('settings.sispTokenization.sandbox')}
                <Chip size="small" label={sandboxCount} color="info" />
              </Box>
            }
          />
          <Tab
            value="production"
            icon={<ProductionIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('settings.sispTokenization.production')}
                <Chip size="small" label={productionCount} color="success" />
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TokenIcon color={activeTab === 'sandbox' ? 'info' : 'success'} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{filteredTokenizations.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeTab === 'sandbox' ? t('settings.sispTokenization.configsSandbox') : t('settings.sispTokenization.configsProduction')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Environment Info Alert */}
      {activeTab === 'sandbox' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{t('settings.sispTokenization.sandboxAlert')}</strong>{' '}
          Base URL: https://mc.vinti4net.cv/BizMPIOnUsSisp/
        </Alert>
      )}
      {activeTab === 'production' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{t('settings.sispTokenization.productionAlert')}</strong>{' '}
          Base URL: https://www.vinti4net.cv/bizmpi/
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>POS ID</TableCell>
                <TableCell>POS Auth Code</TableCell>
                <TableCell>Request URL</TableCell>
                <TableCell>Cancel URL</TableCell>
                <TableCell>Payment URL</TableCell>
                <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTokenizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('settings.sispTokenization.noConfigs', { env: activeTab === 'sandbox' ? t('settings.sispTokenization.sandbox') : t('settings.sispTokenization.production') })}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenEdit()}
                      sx={{ mt: 2 }}
                    >
                      {t('settings.sispTokenization.addConfig')}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTokenizations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tokenization) => (
                  <TableRow key={tokenization.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {tokenization.pos}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        ••••••••
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {tokenization.requestUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {tokenization.cancelUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {tokenization.paymentUrl}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(tokenization)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(tokenization)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredTokenizations.length > 0 && (
          <TablePagination
            component="div"
            count={filteredTokenizations.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
          />
        )}
      </Paper>

      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formData.environment === 'sandbox' ? (
            <SandboxIcon color="info" />
          ) : (
            <ProductionIcon color="success" />
          )}
          {selected ? t('settings.sispTokenization.editTokenization') : t('settings.sispTokenization.newTokenization')} - {formData.environment === 'sandbox' ? t('settings.sispTokenization.sandbox') : t('settings.sispTokenization.production')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="POS ID"
                value={formData.pos}
                onChange={(e) => setFormData({ ...formData, pos: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                placeholder="Ex: 90041"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="POS Auth Code"
                value={formData.posAuthCode}
                onChange={(e) => setFormData({ ...formData, posAuthCode: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                type="password"
                placeholder={t('settings.sispTokenization.authCodePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.environment}
                onChange={(e) => {
                  const newEnv = e.target.value as Environment;
                  const defaultUrls = getDefaultUrls(newEnv);
                  setFormData({
                    ...formData,
                    environment: newEnv,
                    ...defaultUrls,
                  });
                }}
              >
                <MenuItem value="sandbox">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SandboxIcon fontSize="small" color="info" />
                    {t('settings.sispTokenization.sandboxTests')}
                  </Box>
                </MenuItem>
                <MenuItem value="production">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ProductionIcon fontSize="small" color="success" />
                    {t('settings.sispTokenization.production')}
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sispTokenization.requestUrl')}
                value={formData.requestUrl}
                onChange={(e) => setFormData({ ...formData, requestUrl: e.target.value })}
                placeholder="https://"
                helperText={t('settings.sispTokenization.requestUrlHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sispTokenization.cancelUrl')}
                value={formData.cancelUrl}
                onChange={(e) => setFormData({ ...formData, cancelUrl: e.target.value })}
                placeholder="https://"
                helperText={t('settings.sispTokenization.cancelUrlHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sispTokenization.paymentUrl')}
                value={formData.paymentUrl}
                onChange={(e) => setFormData({ ...formData, paymentUrl: e.target.value })}
                placeholder="https://"
                helperText={t('settings.sispTokenization.paymentUrlHelper')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.pos || !formData.posAuthCode}
          >
            {selected ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SispTokenizationPage;
